import React, { FC, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";

import { routes } from "@app/routing";
import { UPSAPIContext } from "@app/provider";
import { SpinnerView } from "@app/components";
import { ButtonNoShadow } from "@app/components/buttonNoShadow";
import { Notification } from "@app/components";
import acknowledgements from "@etc/acknowledgements.json";
import { useUPSHeapEvent } from "@app/ups/hooks";
import { HEAP_EVENTS } from "@app/provider/types";
import { logMsg } from "@app/utils/logMsg";

const messages = defineMessages({
  upsUpdatePaymentMethodUpdateButton: {
    id: "upsUpdatePaymentUpdateButton",
    defaultMessage: "Update",
  },
  upsUpdateFailedTitle: {
    id: "upsUpdateFailedTitle",
    defaultMessage: "Update your payment method",
  },
  upsUpdateFailedDescription: {
    id: "upsUpdateFailedDescription",
    defaultMessage:
      "There is an issue with your payment method. Please contact your payment provider for more details or update your payment method.",
  },
  upsUpdateAcknowledgements: {
    id: "upsUpdateAcknowledgements",
    defaultMessage:
      "You can review important information from Natera on our {termsOfUse} and {privacyPolicy}",
  },
  upsUpdateTermsOfService: {
    id: "upsUpdateTermsOfService",
    defaultMessage: "Terms of Service",
  },
  upsUpdatePrivacyPolicy: {
    id: "upsUpdatePrivacyPolicy",
    defaultMessage: "Privacy Policy",
  },
});

const UPSUpdatePaymentMethodForm: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    upsApiProviderState: { guestValidationData },
    updatePaymentMethod,
  } = useContext(UPSAPIContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const stripe = useStripe();
  const elements = useElements();
  const { trackUPSHeapEvent } = useUPSHeapEvent();

  const handleUpdatePaymentMethod = async (setupIntentId: string) => {
    try {
      const paymentPlanId = guestValidationData?.paymentPlanId;

      if (!paymentPlanId) {
        navigate(routes.upsGuestLogin);
        return;
      }

      await updatePaymentMethod({
        paymentPlanId,
        setupIntentId,
      });
      navigate(routes.upsUpdatePaymentMethodSuccess);
    } catch {
      logMsg("error", "ups.payment", "Error updating payment plan request");
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePaymentRequest = async () => {
    const isReady = stripe && elements;
    if (!isReady) return;

    setLoading(true);
    setError(false);

    try {
      trackUPSHeapEvent({
        eventKey: HEAP_EVENTS.ups_click_update_payment_method,
      });
      const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });

      if (error) {
        logMsg("error", "ups.payment", "Stripe confirm error", {
          errorType: error.type,
        });
        setError(true);
        setLoading(false);
      } else if (setupIntent) {
        handleUpdatePaymentMethod(setupIntent.id);
      }
    } catch {
      logMsg("error", "ups.payment", "Unexpected error");
      setError(true);
      setLoading(false);
    }
  };

  const renderAcknowledgements = () => {
    return intl.formatMessage(messages.upsUpdateAcknowledgements, {
      termsOfUse: (
        <a
          href={acknowledgements.links.termsOfUse}
          key="termsOfUse"
          target="_blank"
          rel="noreferrer"
        >
          {intl.formatMessage(messages.upsUpdateTermsOfService)}
        </a>
      ),
      privacyPolicy: (
        <a
          href={acknowledgements.links.privacyPolicy}
          key="privacyPolicy"
          target="_blank"
          rel="noreferrer"
        >
          {intl.formatMessage(messages.upsUpdatePrivacyPolicy)}
        </a>
      ),
    });
  };

  return (
    <>
      <SpinnerView isLoading={loading} />
      <div className="ups-update-payment-method__payment-form">
        {error && (
          <Notification
            className="ups-update-payment-method__notification"
            titleElem={intl.formatMessage(messages.upsUpdateFailedTitle)}
            type="error"
          >
            {intl.formatMessage(messages.upsUpdateFailedDescription)}
          </Notification>
        )}
        <PaymentElement />
        <p className="ups-update-payment-method__terms">
          {renderAcknowledgements()}
        </p>
        <ButtonNoShadow
          raised
          className="ups-update-payment-method__button"
          onClick={handleUpdatePaymentRequest}
        >
          {intl.formatMessage(messages.upsUpdatePaymentMethodUpdateButton)}
        </ButtonNoShadow>
      </div>
    </>
  );
};

export default UPSUpdatePaymentMethodForm;
